import { Md5 } from 'ts-md5';
import { Downloader, warp_fx, video_engine } from '@picsart/video-sdk';
import PQueue from 'p-queue';

import { ADJUST_EFFECT_ID } from 'pages/Batch/sections/Sidebar/AdjustTool/constants';
import type { IProcessResult } from './toolchain';

export interface AdjustProcessAttrs {
  params: Record<string, number>;
}

const EFFECTS_BASE_URL = 'https://effres.picsart.com/warp-effects/effects/';
const RESOURCES_BASE_URL = 'https://effres.picsart.com/warp-effects/res/';

const adjustQueue = new PQueue({ concurrency: 1 });

function generateEffectUrl(effectId: string): string {
  return `${EFFECTS_BASE_URL}${Md5.hashStr(effectId)}.json`;
}

function generateResourceUrl(fileName: string): string {
  const ext = fileName.match(/(?:\.([^.]+))?$/)?.[0] || '';
  const baseName = fileName.replace(ext, '');
  return `${RESOURCES_BASE_URL}${Md5.hashStr(baseName)}${ext}`;
}

let cachedDescriptor: warp_fx.core.BuilderDescriptor | null = null;

export async function getAdjustDescriptor(): Promise<warp_fx.core.BuilderDescriptor> {
  if (cachedDescriptor) return cachedDescriptor;

  const url = generateEffectUrl(ADJUST_EFFECT_ID);
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch adjust effect: ${response.status}`);
  const data = await response.json();
  const dataString = typeof data === 'string' ? data : JSON.stringify(data);

  const factory = warp_fx.core.BuilderDescriptorFactory.factory();
  const descriptor = factory.createBuilderDescriptorFromData(dataString, '', ADJUST_EFFECT_ID);

  const { requiredResources } = descriptor;
  if (requiredResources?.length > 0) {
    const downloader = new Downloader();
    const resources = await Promise.all(
      requiredResources.map(async (rd: warp_fx.resources.FileResourceDescriptor | warp_fx.resources.ImageResourceDescriptor) => {
        const resUrl = generateResourceUrl(rd.fileName);
        const path = await downloader.download(resUrl);
        if (rd instanceof warp_fx.resources.FileResourceDescriptor) {
          return warp_fx.resources.FileReaderResource.create(path, rd);
        }
        return warp_fx.resources.ImageFileResource.create(path, rd);
      }),
    );
    descriptor.setResources(resources);
  }

  cachedDescriptor = descriptor;
  return descriptor;
}

async function renderAdjust(imageUrl: string, width: number, height: number, params: Record<string, number>): Promise<HTMLCanvasElement> {
  const offscreen = new OffscreenCanvas(width, height);

  const project = video_engine.project.Project.create();
  const resource = video_engine.resources.PhotoResource.create(imageUrl);
  const photoLayer = video_engine.layers.PhotoLayer.create(resource);
  photoLayer.desiredDuration = 1;
  photoLayer.desiredResolution = [width, height];

  const composition = video_engine.project.Composition.create();
  composition.desiredResolution = [width, height];
  composition.addLayer(photoLayer);
  project.active = composition;

  const descriptor = await getAdjustDescriptor();
  const effectResource = video_engine.resources.EffectResource.create(descriptor);
  const effectComponent = video_engine.components.EffectComponent.create(effectResource, `adjust_effect__${ADJUST_EFFECT_ID}`);
  photoLayer.addComponent(effectComponent);

  const paramsWithHsl = { ...params, isHslUsed: 1 };
  Object.entries(paramsWithHsl).forEach(([name, value]) => {
    try {
      effectComponent.property(name).value = String(value);
    } catch {
      // parameter may not exist
    }
  });

  const frameRenderer = video_engine.media.FrameRenderer.create(project);

  try {
    frameRenderer.setPreviewCanvas(offscreen);
    await frameRenderer.drawFrame(0);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(offscreen, 0, 0);
    return canvas;
  } finally {
    frameRenderer.delete();
  }
}

export function cancelAdjustQueue() {
  adjustQueue.clear();
}

export function getAdjustProcess({ params }: AdjustProcessAttrs) {
  return async (image: HTMLImageElement, isCancel: () => boolean): Promise<IProcessResult> => {
    const result = await adjustQueue.add(
      async () => {
        if (isCancel()) throw new Error('cancelled');

        const canvas = await renderAdjust(image.src, image.naturalWidth, image.naturalHeight, params);

        const blob = await new Promise<Blob>(res => {
          canvas.toBlob(b => res(b!), 'image/webp');
        });
        return { blob, result: URL.createObjectURL(blob) };
      },
      { throwOnTimeout: true },
    );

    return result as IProcessResult;
  };
}
